import React, { useMemo } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'
import { eventGroupsOnWeb } from '@lib/feature-flags'

const EventsList = ({
  instances,
  isLoading,
  isError,
  selectedDate,
  availability,
  onDateSelect
}) => {
  // Check if user is in UK timezone
  const isUKTimezone = useMemo(() => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    return userTimezone === 'Europe/London'
  }, [])

  // Find next available date
  const findNextAvailableDate = () => {
    if (!availability?.dates_with_events) {
      return null
    }

    const currentDate = new Date(selectedDate)
    const currentDateStr = currentDate.toISOString().split('T')[0] // Format as YYYY-MM-DD

    // Get all available dates that are after the current selected date
    const availableDates = availability.dates_with_events
      .filter(dateStr => {
        const date = new Date(dateStr + 'T00:00:00')
        const current = new Date(currentDateStr + 'T00:00:00')
        return date > current
      })
      .sort()

    return availableDates.length > 0 ? availableDates[0] : null
  }

  const nextAvailableDate = useMemo(
    () => findNextAvailableDate(),
    [availability, selectedDate]
  )

  // Fetch events for the next available date when current date has no events
  const shouldFetchNextDateEvents =
    (!instances || instances.length === 0) && nextAvailableDate && !isLoading
  const { data: nextDateInstances, isLoading: isLoadingNextDate } = useSWR(
    shouldFetchNextDateEvents ? `/whatson/${nextAvailableDate}` : null,
    fetcher
  )

  // Format the selected date for display
  const formatDateForDisplay = () => {
    if (!selectedDate) return 'this day'

    try {
      const date =
        selectedDate instanceof Date ? selectedDate : new Date(selectedDate)
      return date.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      })
    } catch (e) {
      return 'this day'
    }
  }

  // Helper to format UTC time to UK timezone (Europe/London)
  const formatTime = instance => {
    // The 'start' field contains UTC time (ends with Z)
    const utcTimeString = instance.start

    if (!utcTimeString) return ''

    try {
      // Parse the UTC datetime string
      const utcDate = new Date(utcTimeString)

      // Check if the date is valid
      if (isNaN(utcDate.getTime())) {
        console.warn('Invalid date:', utcTimeString)
        return ''
      }

      // Convert to UK timezone (Europe/London)
      // This automatically handles GMT/BST transitions
      return utcDate.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Europe/London'
      })
    } catch (error) {
      console.error('Error formatting time:', error, 'UTC time:', utcTimeString)
      return ''
    }
  }

  // Helper to get event image URL (prefer square medium for better quality)
  const getEventImageUrl = event => {
    // Try to get a medium square variant first
    if (event.image_urls?.standard_image?.variants?.square?.medium) {
      return event.image_urls.standard_image.variants.square.medium
    }
    // Then try the default
    if (event.image_urls?.default) {
      return event.image_urls.default
    }
    // Fallback to original
    if (event.image_urls?.standard_image?.original) {
      return event.image_urls.standard_image.original
    }
    return null
  }

  // Helper to extract subtitle info (e.g. "Led by", "Dir:", etc.)
  const getSubtitle = instance => {
    // Check for various subtitle patterns in description or other fields
    if (instance.event.sub_title) {
      return instance.event.sub_title
    }
    // You can add more logic here based on your data structure
    return null
  }

  // Helper to get rating/certificate
  const getRating = event => {
    // Check if event has a certificate or rating
    if (event.certificate) {
      return event.certificate
    }
    // Check in category list for ratings like PG, 15, etc.
    const ratings = ['U', 'PG', '12A', '12', '15', '18']
    const foundRating = event.category_list?.find(cat =>
      ratings.includes(cat.toUpperCase())
    )
    return foundRating?.toUpperCase()
  }

  // Component to display events list - must be defined before usage
  const EventsListDisplay = ({ instances, isDifferentDate = false }) => (
    <div className='divide-y divide-gray-200 dark:divide-gray-800 rounded'>
      {instances.map((instance, index) => {
        const imageUrl = getEventImageUrl(instance.event)
        const subtitle = getSubtitle(instance)
        const rating = getRating(instance.event)
        const isFreeOfCharge = instance.free_of_charge === true
        const isNotOnSale = !instance.on_sale || instance.locked
        const isSoldOut =
          instance.available === 0 && !isFreeOfCharge && !isNotOnSale
        const isLimitedAvailability = instance.limited_availability === true

        return (
          <a
            key={instance.id}
            href={`/events/${instance.event.slug}`}
            className='block group hover:bg-gray-100 dark:hover:bg-gray-900/30 transition-colors'
          >
            <div className='flex gap-3 p-3'>
              {/* Event Image with time overlay */}
              <div className='relative shrink-0'>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={instance.event.name}
                    className='w-20 h-20 object-cover rounded'
                    loading='lazy'
                  />
                ) : (
                  <div className='w-20 h-20 bg-gray-200 dark:bg-gray-800 rounded flex items-center justify-center'>
                    <svg
                      className='w-10 h-10 text-gray-400 dark:text-gray-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
                      />
                    </svg>
                  </div>
                )}

                {/* Time overlay */}
                <div className='absolute bottom-0 left-0 bg-black/80 text-white px-2 py-1 text-xs font-medium'>
                  {formatTime(instance)}
                </div>

                {/* Status overlay */}
                {isFreeOfCharge ? (
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <div className='bg-green-600 text-white text-xs font-bold px-2 py-1 rounded transform -rotate-12 whitespace-nowrap'>
                      FREE
                    </div>
                  </div>
                ) : isSoldOut && !isNotOnSale ? (
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <div className='bg-red-600 text-white text-xs font-bold px-2 py-1 rounded transform -rotate-12 whitespace-nowrap'>
                      SOLD OUT
                    </div>
                  </div>
                ) : null}

                {/* Limited availability overlay */}
                {isLimitedAvailability && !isSoldOut && (
                  <div className='absolute inset-0 flex items-center justify-center'>
                    <div className='bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded transform -rotate-12 whitespace-nowrap'>
                      LIMITED
                    </div>
                  </div>
                )}

                {/* Rating badge */}
                {rating && (
                  <div className='absolute top-1 right-1'>
                    <div className='bg-yellow-500 text-black text-xs font-bold w-7 h-7 flex items-center justify-center pentagon'>
                      {rating}
                    </div>
                  </div>
                )}
              </div>

              {/* Event Details */}
              <div className='flex-grow min-w-0 py-1'>
                {/* Series label only: the day view always lists each session */}
                {eventGroupsOnWeb() && instance.event.group && (
                  <p className='text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5'>
                    {instance.event.group.name}
                  </p>
                )}
                <h4 className='font-serif text-base text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors leading-tight'>
                  {instance.event.name}
                </h4>

                {subtitle && (
                  <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                    {subtitle}
                  </p>
                )}

                {/* Show description snippet for non-film events */}
                {!rating && instance.event.description && (
                  <p
                    className='text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2'
                    dangerouslySetInnerHTML={{
                      __html: instance.event.description
                    }}
                  />
                )}
              </div>

              {/* Arrow indicator */}
              <div className='shrink-0 flex items-center'>
                <svg
                  className='w-5 h-5 text-gray-400 dark:text-gray-600 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9 5l7 7-7 7'
                  />
                </svg>
              </div>
            </div>
          </a>
        )
      })}
    </div>
  )

  if (isLoading)
    return (
      <div className='p-4 text-gray-600 dark:text-gray-400'>
        Loading events...
      </div>
    )
  if (isError)
    return (
      <div className='p-4 text-red-600 dark:text-red-400'>
        Error loading events
      </div>
    )
  if (!instances || instances.length === 0) {
    return (
      <div>
        <div className='p-4 text-gray-600 dark:text-gray-400'>
          <div>
            No events on {formatDateForDisplay()}.
            {nextAvailableDate && onDateSelect && (
              <span>
                {' '}
                Norden Farm is open again on{' '}
                <button
                  onClick={() =>
                    onDateSelect(new Date(nextAvailableDate + 'T00:00:00'))
                  }
                  className='text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-100 underline'
                >
                  {new Date(nextAvailableDate + 'T00:00:00').toLocaleDateString(
                    'en-GB',
                    {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    }
                  )}
                </button>
                .
              </span>
            )}
          </div>
        </div>

        {/* Show events from next available date */}
        {nextAvailableDate &&
          nextDateInstances &&
          nextDateInstances.length > 0 && (
            <div>
              <div className='px-4 pb-2 text-sm font-medium text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-gray-700 pt-3'>
                Events on{' '}
                {new Date(nextAvailableDate + 'T00:00:00').toLocaleDateString(
                  'en-GB',
                  {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long'
                  }
                )}
                :
              </div>
              <EventsListDisplay
                instances={nextDateInstances}
                isDifferentDate={true}
              />
            </div>
          )}

        {/* Loading state for next date events */}
        {nextAvailableDate && isLoadingNextDate && (
          <div className='p-4 text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700'>
            Loading events for next available date...
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      {/* Timezone warning for non-UK users */}
      {!isUKTimezone && (
        <div className='bg-yellow-900/20 border border-yellow-700/50 text-yellow-200 px-3 py-2 mb-2 text-xs '>
          <div className='flex items-center gap-2'>
            <svg
              className='w-4 h-4 flex-shrink-0'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                clipRule='evenodd'
              />
            </svg>
            <span>All times shown are in UK time (Europe/London)</span>
          </div>
        </div>
      )}

      <EventsListDisplay instances={instances} />
    </div>
  )
}

export default EventsList
